import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch } from './api';
import { fetchChart, peekCache } from './chartCache';

// Fetches a rendered candlestick SVG from bse2-backend's /chart/:link/:period
// (which itself gets OHLCV from the scraper and asks the shared chart-service
// to render it), and draws a lightweight crosshair + tooltip on hover using
// the plot-bounds metadata returned alongside the SVG and the raw candle
// list fetched separately for date/OHLC lookups.
const ChartPanel = ({ link, period = 'daily', reloadKey = 0, onToggleDetails, pickMode = '', onPricePick, markers = [] }) => {
  const [svg, setSvg] = useState('');
  const [plot, setPlot] = useState(null);
  const [candles, setCandles] = useState([]);
  const [error, setError] = useState(false);
  const [hover, setHover] = useState(null);
  const [imageHeight, setImageHeight] = useState(0);
  const imgWrapRef = useRef(null);

  useEffect(() => {
    if (!link) return;
    let cancelled = false;
    setError(false);
    setHover(null);

    // Cached candles-only chart: on a cache hit the previous chart swaps
    // instantly; on a miss the previous chart stays visible until the new
    // one arrives (no blank flash between scripts).
    const cached = peekCache(link, period, 'candles');
    if (cached) {
      setSvg(cached.svg || '');
      setPlot(cached.plot || null);
      setCandles(cached.candles || []);
    }

    fetchChart(link, period, 'candles')
      .then((value) => {
        if (cancelled) return;
        setSvg(value.svg || '');
        setPlot(value.plot || null);
        setCandles(value.candles || []);
        setError(false);
      })
      .catch(() => { if (!cancelled && !cached) setError(true); });

    return () => { cancelled = true; };
  }, [link, period, reloadKey]);

  const dataUrl = useMemo(() => {
    if (!svg) return '';
    // Without this the SVG keeps its own 960x610 aspect inside the img box
    // (default preserveAspectRatio="meet") and letterboxes with blank bands
    // above/below whenever the pane is taller than that ratio.
    const stretched = svg.replace(/<svg\b/, "<svg preserveAspectRatio='none'");
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(stretched)))}`;
  }, [svg]);

  const visibleMarkers = useMemo(() => {
    if (!plot || !(imageHeight > 0) || !(plot.maxPrice > plot.minPrice)) return [];
    return markers
      .filter((marker) => Number.isFinite(Number(marker.price))
        && Number(marker.price) >= plot.minPrice && Number(marker.price) <= plot.maxPrice)
      .map((marker) => ({
        ...marker,
        top: ((plot.top + ((plot.maxPrice - Number(marker.price)) / (plot.maxPrice - plot.minPrice)) * plot.height) / imageHeight) * 100,
      }));
  }, [markers, plot, imageHeight]);

  if (!link) return null;
  if (error) return <div className="chart-panel-error">Chart unavailable</div>;
  if (!dataUrl) return <div className="chart-panel-loading">Loading chart…</div>;

  const handleMove = (event) => {
    if (!plot || candles.length === 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const naturalWidth = event.currentTarget.naturalWidth || rect.width;
    const naturalHeight = event.currentTarget.naturalHeight || rect.height;
    const chartX = ((event.clientX - rect.left) / rect.width) * naturalWidth;
    const chartY = ((event.clientY - rect.top) / rect.height) * naturalHeight;
    if (chartX < plot.left || chartX > plot.left + plot.width) return setHover(null);
    // The image may also contain the fundamentals panels below the candles;
    // the crosshair belongs to the candle area only.
    const candleAreaHeight = plot.financeHeight || naturalHeight;
    if (chartY > candleAreaHeight) return setHover(null);

    const relevantCandles = period === 'daily'
      ? candles.slice(-plot.count)
      : candles;
    const idx = Math.round(((chartX - plot.left) / plot.width) * (relevantCandles.length - 1));
    const candle = relevantCandles[idx];
    if (!candle) return setHover(null);

    const lineX = plot.left + (idx / Math.max(1, relevantCandles.length - 1)) * plot.width;
    const priceAtY = plot.maxPrice - ((chartY - plot.top) / plot.height) * (plot.maxPrice - plot.minPrice);

    setHover({
      candle,
      lineXPct: (lineX / naturalWidth) * 100,
      lineHeightPct: (candleAreaHeight / naturalHeight) * 100,
      cursorYPct: ((chartY - plot.top) / plot.height) * 100,
      price: priceAtY,
    });
  };

  const handleClick = (event) => {
    if (pickMode === 'stop') {
      let price = hover?.price;
      if (price == null && plot) {
        const rect = event.currentTarget.getBoundingClientRect();
        const naturalHeight = event.currentTarget.naturalHeight || rect.height;
        const chartY = ((event.clientY - rect.top) / rect.height) * naturalHeight;
        if (chartY >= plot.top && chartY <= plot.top + plot.height) {
          price = plot.maxPrice - ((chartY - plot.top) / plot.height) * (plot.maxPrice - plot.minPrice);
        }
      }
      if (Number.isFinite(price)) onPricePick?.(price);
      return;
    }
    onToggleDetails?.();
  };

  return (
    <div className="chart-panel" ref={imgWrapRef}>
      <img
        className={`chart-panel-image ${pickMode ? 'picking-price' : ''}`}
        src={dataUrl}
        alt=""
        draggable="false"
        title={pickMode === 'stop' ? 'Click the chart to set stop loss' : 'Click to show/hide the quarterly figures'}
        onLoad={(event) => setImageHeight(event.currentTarget.naturalHeight || event.currentTarget.clientHeight || 0)}
        onClick={handleClick}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      />
      {visibleMarkers.map((marker) => (
        <div
          key={`${marker.label || 'marker'}-${marker.price}`}
          className={`chart-price-marker ${marker.kind || 'entry'}`}
          style={{ top: `${marker.top}%` }}
        >
          <span>{marker.label || `₹${Number(marker.price).toFixed(2)}`}</span>
        </div>
      ))}
      {hover && (
        <>
          <div
            className="chart-panel-crosshair"
            style={{ left: `${hover.lineXPct}%`, height: `${hover.lineHeightPct}%` }}
          />
          <div className="chart-panel-tooltip" style={{ left: `${hover.lineXPct}%` }}>
            <div>{hover.candle.time}</div>
            <div>O {hover.candle.open} H {hover.candle.high}</div>
            <div>L {hover.candle.low} C {hover.candle.close}</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ChartPanel;
